import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import {
  DataGridPro,
  GridFilterModel,
  GridRowModel,
  GridSortModel
} from "@mui/x-data-grid-pro";
import { useContext, useEffect, useState, useMemo } from "react";
import {
  Nav,
  NavDropdown,
  Col,
  Container,
  Row,
  Spinner,
  Navbar,
  Modal,
  Form,
  Button
} from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import {
  Action,
  ExtendedRepositoryObject,
  GetRepositoryObjectsInput,
  RepositoryObject,
  RepositoryObjectFolder,
  RepositoryTree
} from "../../backend/types";
import { PmxActivityDetailsContext } from "../../contexts/pmxActivityDetailsProvider";
import { MenuContext, columnsRender } from "../../helpers/tableViewHelper";
import Error from "../abstractComponents/error";
import ErrorBoundary from "../abstractComponents/errorBoundary";
import { useAuth } from "../authContextProvider";
import NavBarPmxActivity from "../pmxActivity/navbarPmxActivity";
import { TreeViewListFilesInRepo } from "../pmxActivity/treeViewListFilesInRepo";
import RestoreIcon from "@mui/icons-material/Restore";
import { useRestoreFilePathMutation } from "../../backend/hooks/history/mutationRestoreFilePath";
import Success from "../abstractComponents/success";
import EditAction from "../editAction";
import { useGetLastActionQuery } from "../../backend/hooks/action/queryGetActions";
import { FilePreview } from "../pmxActivity/filePreview";
import TracebilityReport from "../contextMenu/tracebilityReport";
import ActionsOfFiles from "../contextMenu/actionsOfFiles";
import CreateHistoryLabel from "../history/createHistoryLabel";
import { useGetActionQuery } from "../../backend/hooks/action/queryGetAction";
import { CreateInteractiveModal } from "../interactive/createInteractiveModal";
import { convertToIsoString } from "../../helpers/stringHelper";
import { checkIfActionIsNotEditable } from "../../helpers/actionHelper";
import UserManualLink from "../abstractComponents/usermanual";
import { useRepositoryObjects } from "../../backend/hooks/pmxActivity/queryGetRepositoryObjects";
import {
  buildSortFilterRequestParams,
  isValidFilter
} from "../../helpers/urlHelper";

export function HistoryView() {
  const useQuery = () => {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  };
  const query: any = useQuery();
  const historyDate = query.get("historicalDate");

  const { activityId, activityData, isActivityLoading } = useContext(
    PmxActivityDetailsContext
  );

  const [currentAction, setCurrentAction] = useState<Action | null>(null);
  const [currentSelectedPath, setCurrentSelectedPath] = useState("/");
  const [currentSelectedVersionId, setCurrentSelectedVersionId] = useState("");
  const [currentSelectedRevision, setCurrentSelectedRevision] = useState("");
  const [currentSelectedRepoId, setCurrentSelectedRepoId] = useState<string>(
    activityData?.mainRepository.id ? activityData.mainRepository.id : ""
  );
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [comment, setComment] = useState("");
  const [isRestoreModalVisible, setIsRestoreModalVisible] = useState(false);
  const [isActionOfFilesVisible, setIsActionOfFilesVisible] = useState(false);
  const [isTracebilityReportVisible, setIsTracebilityReportVisible] =
    useState(false);
  const [openPropertiesModal, setOpenPropertiesModal] =
    useState<boolean>(false);
  const [actionId, setActionId] = useState(query.get("actionId"));
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(
    undefined
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [pageSize, setPageSize] = useState(100);
  const [hasMounted, setHasMounted] = useState(false);
  const [lastRequestParams, setLastRequestParams] = useState<any>(null);

  // Repository Objects Query
  const {
    callMultipleRepository,
    callRepositoryObjects,
    tableViewFiles,
    treeHierarchy,
    isGetRepositoryObjectsLoading,
    getRepositoryObjectsError,
    historicalDatetime,
    foldersAndCount
  } = useRepositoryObjects(activityData?.mainRepository?.id);
  const [page, setPage] = useState(
    foldersAndCount.find(
      (folder: RepositoryObjectFolder) =>
        folder.folderName === currentSelectedPath
    )?.currentPageNumber ?? 0
  );

  useEffect(() => {
    if (activityData) {
      const repoIds = [
        ...(Array.isArray(activityData.readOnlyRepositories)
          ? activityData.readOnlyRepositories.map((repo: any) => repo.id)
          : []),
        activityData.mainRepository.id
      ];
      const repositoryObjectsInput: GetRepositoryObjectsInput[] = repoIds.map(
        (repoId: string) => ({
          repoId: String(repoId),
          historicalInput: {
            ...(actionId && { historicActionId: actionId }),
            ...(historyDate && {
              historicDatetime: convertToIsoString(historyDate)
            })
          }
        })
      );
      callMultipleRepository(repositoryObjectsInput);
      setCurrentSelectedRepoId(activityData?.mainRepository.id);
    }
  }, [activityData]);

  // Unified effect to handle all relevant changes and avoid duplicate requests
  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true);
      return;
    }

    // If filter is set and valid, use it; otherwise, ignore filter
    const useFilter =
      filterModel &&
      (filterModel.items.length === 0 ||
        filterModel.items.every(isValidFilter));

    const params = buildSortFilterRequestParams({
      repoId: currentSelectedRepoId,
      location: currentSelectedPath,
      pageNumber: page + 1,
      pageSize,
      sortModel,
      filterModel: useFilter ? filterModel : undefined
    });

    let shouldResetPage = false;

    if (lastRequestParams) {
      // Compare sort and filter models only
      const lastSort = JSON.stringify(lastRequestParams.sort || []);
      const currSort = JSON.stringify(params.sort || []);
      const lastFilter = JSON.stringify(lastRequestParams.filter || {});
      const currFilter = JSON.stringify(params.filter || {});

      if (lastSort !== currSort || lastFilter !== currFilter) {
        shouldResetPage = true;
      }
    }

    if (shouldResetPage) {
      params.pageNumber = 1;
      setPage(0); // Also reset local page state
    }

    callRepositoryObjects({
      variables: {
        ...params,
        historicalInput: {
          ...(actionId && { historicActionId: actionId }),
          ...(historyDate && {
            historicDatetime: convertToIsoString(historyDate)
          })
        }
      }
    });
    setLastRequestParams(params);
  }, [sortModel, page, pageSize, filterModel]);

  const extendTableRecordDetails = (): ExtendedRepositoryObject[] => {
    return tableViewFiles.map((object: RepositoryObject) => {
      let relativeName = object.name;

      if (
        currentSelectedPath !== "/" &&
        currentSelectedPath.endsWith("/") &&
        object.name.startsWith(currentSelectedPath)
      ) {
        relativeName = object.name.slice(currentSelectedPath.length);
        if (relativeName.startsWith("/")) {
          relativeName = relativeName.slice(1);
        }
        if (!relativeName) {
          const segments = currentSelectedPath.split("/").filter(Boolean);
          relativeName = segments[segments.length - 1] || object.name;
        }
      } else {
        const segments = object.name.split("/");
        relativeName =
          segments[segments.length - 1] ||
          segments[segments.length - 2] ||
          object.name;
      }
      relativeName = relativeName.replace(/\/$/, "");

      return {
        ...object,
        absolutePath: object.name,
        name: relativeName
      };
    });
  };

  // Action Queries
  const { getLastAction, isActionLoading, errorAction } =
    useGetLastActionQuery();

  const {
    getAction,
    isActionLoading: isActionLoadingPulled,
    errorAction: errorActionPulled
  } = useGetActionQuery();

  // TODO: Fix backend, getActions should be able to do what getAction does with the right parameters,
  //  then we dont need 2 separate queries here
  useEffect(() => {
    if (historyDate) {
      getLastAction({
        variables: {
          activityId: activityId,
          createdBefore: convertToIsoString(historyDate)
        }
      }).then(({ data }) => {
        const lastAction = data?.getActions?.actions[0] || null;
        setActionId(lastAction?.id);
        setCurrentAction(lastAction);
      });
    } else if (actionId) {
      getAction({
        variables: {
          actionId: actionId
        }
      }).then(({ data }) => {
        setCurrentAction(data.getAction);
      });
    }
  }, [historyDate]);

  const {
    restoreFilePathMutation,
    isLoadingRestoreMutation,
    errorRestoreMutation,
    dataMutation
  } = useRestoreFilePathMutation();
  const restoreSelectedPaths = () => {
    restoreFilePathMutation({
      variables: {
        paths: selectedRows,
        activityId: activityId,
        historicDatetime: historyDate ? historyDate : historicalDatetime,
        comment: comment
      }
    }).then(() => {
      setSelectedRows([]);
      setComment("");
    });
    setIsRestoreModalVisible(false);
  };

  const onNodeSelectFunction = ({
    path,
    versionId = "",
    revision = "",
    repoId = currentSelectedRepoId,
    pageNumber
  }: {
    path: string;
    versionId: string;
    revision: string;
    repoId: string;
    pageNumber: number;
  }) => {
    setCurrentSelectedPath(path);
    // Query only for folders
    if (path.endsWith("/")) {
      callRepositoryObjects({
        variables: {
          repoId: String(repoId),
          location: path,
          pageNumber: pageNumber,
          historicalInput: {
            ...(actionId
              ? { historicActionId: actionId }
              : { historicDatetime: convertToIsoString(historyDate) })
          }
        }
      });
    }
    setCurrentSelectedVersionId(versionId!);
    setCurrentSelectedRevision(revision!);
    setCurrentSelectedRepoId(repoId);
  };

  const isReadOnlyRepository = () => {
    if (tableViewFiles.length > 0) {
      return (
        tableViewFiles[0].repository.id !== activityData?.mainRepository.id
      );
    }
  };

  const findRepositoryObjectFromList = () => {
    return tableViewFiles.find(
      (object) =>
        object.name === currentSelectedPath &&
        object.versionId === currentSelectedVersionId
    );
  };

  // TODO: don't create a refetchFunction, just use the prebuild refetch from apollo
  const refetchAction = () => {
    if (historyDate) {
      getLastAction({
        variables: {
          activityId: activityId,
          createdBefore: convertToIsoString(historyDate)
        }
      });
    } else {
      getAction({
        variables: {
          actionId: actionId
        }
      });
    }
  };
  const { decodedToken } = useAuth();

  const folderInfo = foldersAndCount.find(
    (folder: RepositoryObjectFolder) =>
      folder.folderName === currentSelectedPath
  );

  const rowsForCurrentSelectedPath =
    !isGetRepositoryObjectsLoading && folderInfo
      ? (extendTableRecordDetails() as readonly GridRowModel[])
      : [];

  const rowCountForCurrentSelectedPath =
    rowsForCurrentSelectedPath.length === 0 ? 0 : folderInfo?.count ?? 0;

  return (
    <>
      <NavBarPmxActivity activeIndex={4} />

      <div className="app_content pmx_app_content">
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className="float-start pmx_bradcrumbs"
        >
          <Link to={`/activity/${activityId}`}>
            {activityData?.trialNumber}
          </Link>
          <Link to={`/activity/${activityId}/history`}>History</Link>,
          <Typography>Historical View</Typography>
        </Breadcrumbs>
        <br />
        <hr />
        <ErrorBoundary>
          <Container fluid className="browse-pmx-activity-container">
            <h4>Historical View</h4>
            <Row>
              <Col xs="5">
                <div>
                  Viewing historical view for the action ID: <b>{actionId}</b>
                  {actionId && currentAction && (
                    <EditAction
                      actionId={actionId}
                      notEditableAction={checkIfActionIsNotEditable(
                        currentAction?.actionType
                      )}
                    />
                  )}
                </div>
                {currentAction && currentAction.historyLabel?.name && (
                  <div>
                    History label: <b>{currentAction.historyLabel?.name}</b>
                  </div>
                )}
              </Col>
              <Col xs="1">
                <UserManualLink
                  url="/usermanual/interactive"
                  testId="um-actions"
                />
              </Col>
              <Col xs="3">
                <CreateInteractiveModal actionId={actionId} />
              </Col>
              <Col xs="3">
                <Navbar
                  id="history_nav"
                  className="table_view_navbar"
                  collapseOnSelect
                  expand="lg"
                  bg="light"
                  variant="light"
                  style={{ float: "right" }}
                >
                  <Nav>
                    {actionId && (
                      <CreateHistoryLabel
                        activityId={activityId}
                        actionId={actionId}
                        onComplete={() => {
                          refetchAction();
                        }}
                      />
                    )}

                    {(isActionLoading || isActionLoadingPulled) && (
                      <Spinner animation="border" className="spinner_color" />
                    )}
                    {selectedRows.length !== 0 &&
                      Number(currentSelectedRepoId) ===
                        Number(activityData?.mainRepository?.id) && (
                        <NavDropdown.Item
                          onClick={() => {
                            setIsRestoreModalVisible(true);
                          }}
                          id="restoreSelectedPaths"
                          className="nav-link nav_item_button"
                        >
                          <RestoreIcon className="table_view_svg" /> Restore
                        </NavDropdown.Item>
                      )}
                  </Nav>
                </Navbar>
              </Col>
            </Row>

            {isActivityLoading || isLoadingRestoreMutation ? (
              <div className="center_div">
                <Spinner animation="border" className="spinner_color" />
                <p>Loading...</p>
              </div>
            ) : (
              <Row className="pmx-activity-row-container">
                <Col
                  xs="4"
                  className="d-block tree-view-column historical_tree_view"
                >
                  {treeHierarchy?.map((repositoryTree: RepositoryTree) => (
                    <ErrorBoundary key={repositoryTree.repository.id}>
                      <TreeViewListFilesInRepo
                        foldersAndCount={foldersAndCount}
                        repositoryTree={repositoryTree}
                        activityData={activityData}
                        onNodeSelect={onNodeSelectFunction}
                        currentSelectedPath={currentSelectedPath}
                        currentSelectedRepoId={currentSelectedRepoId}
                      />
                    </ErrorBoundary>
                  ))}
                </Col>
                <Col xs="8">
                  <div
                    className="table-view-list-container"
                    style={{ height: "90%" }}
                  >
                    {!currentSelectedPath?.endsWith("/") &&
                    currentSelectedPath !== "" ? (
                      <ErrorBoundary>
                        <FilePreview
                          absolutePath={currentSelectedPath}
                          versionId={currentSelectedVersionId}
                          revision={currentSelectedRevision}
                          setTableMenuWasOpenOnPath={() => {}}
                          setIsActionOfFilesVisible={setIsActionOfFilesVisible}
                          openPropertiesModal={openPropertiesModal}
                          setIsPreselectedRevision={() => {}}
                          setIsTracebilityReportVisible={
                            setIsTracebilityReportVisible
                          }
                          repoId={currentSelectedRepoId}
                        />
                      </ErrorBoundary>
                    ) : (
                      <MenuContext.Provider
                        value={{
                          handleClickComment: () => {},
                          handleClickFile: () => {},
                          handleClickFolder: () => {},
                          onNodeSelect: onNodeSelectFunction
                        }}
                      >
                        <DataGridPro
                          checkboxSelection
                          className="browser_repository_table"
                          classes={
                            isReadOnlyRepository()
                              ? {
                                  virtualScroller: "opacity-60"
                                }
                              : {}
                          }
                          columns={columnsRender(() => {}, true, true, false)}
                          disableSelectionOnClick
                          disableVirtualization={decodedToken["email"].includes(
                            "test-user@bayer.com"
                          )}
                          getRowId={(row) => row.absolutePath}
                          loading={isGetRepositoryObjectsLoading}
                          selectionModel={selectedRows}
                          onSelectionModelChange={(e: any) =>
                            setSelectedRows(e)
                          }
                          onFilterModelChange={(newFilterModel) =>
                            setFilterModel(newFilterModel)
                          }
                          onPageChange={(newPage) => setPage(newPage)}
                          onPageSizeChange={(pageSize) => setPageSize(pageSize)}
                          onSortModelChange={(model) => setSortModel(model)}
                          page={page}
                          pageSize={pageSize}
                          pagination
                          paginationMode={"server"}
                          rowHeight={50}
                          rowCount={rowCountForCurrentSelectedPath}
                          rows={extendTableRecordDetails() as GridRowModel[]}
                        />
                      </MenuContext.Provider>
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </Container>
        </ErrorBoundary>

        <Modal
          size="lg"
          show={isRestoreModalVisible}
          onHide={() => setIsRestoreModalVisible(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Restore Items</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <br />
            <p>
              You will restore the following paths, including all files
              contained within them:
            </p>
            <ul id="list_files">
              {selectedRows.map((absolutePath: String) => {
                let name = currentSelectedPath;
                const file = extendTableRecordDetails().find(
                  (file: ExtendedRepositoryObject) =>
                    file.absolutePath === absolutePath
                );
                if (file) {
                  name = name + file.name;
                }
                return <li key={name}>{name}</li>;
              })}
            </ul>
            <Form.Group className="mb-3" controlId="formBasicName">
              <Form.Label>
                Comment <span className="red_form">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                required
                placeholder="Comment"
                id="set_comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setIsRestoreModalVisible(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={comment === ""}
              onClick={restoreSelectedPaths}
            >
              <RestoreIcon /> Restore Items
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      {isTracebilityReportVisible && treeHierarchy && (
        <ErrorBoundary>
          <TracebilityReport
            activityId={activityId}
            onNodeSelect={onNodeSelectFunction}
            onClose={() => {
              setIsTracebilityReportVisible(false);
            }}
            currentFile={findRepositoryObjectFromList()}
          />
        </ErrorBoundary>
      )}

      {isActionOfFilesVisible && treeHierarchy && (
        <ActionsOfFiles
          absolutePath={currentSelectedPath}
          isHistoricalView={true}
          onClose={() => {
            setIsActionOfFilesVisible(false);
            setOpenPropertiesModal(false);
          }}
          onNodeSelect={onNodeSelectFunction}
          repoId={currentSelectedRepoId}
          versionId={currentSelectedVersionId}
        />
      )}

      {(getRepositoryObjectsError ||
        errorRestoreMutation ||
        errorAction ||
        errorActionPulled) && (
        <Error
          error={
            getRepositoryObjectsError ||
            errorRestoreMutation ||
            errorAction ||
            errorActionPulled
          }
        />
      )}
      {dataMutation && (
        <Success
          message={
            dataMutation?.restoreFilePath?.versionIds.length === 0
              ? "No files needed to be modified"
              : `A total of ${dataMutation?.restoreFilePath?.versionIds.length} files were modified.`
          }
        />
      )}
    </>
  );
}
